import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/atoms/dialog";
import { Button } from "@/components/atoms/button";
import { Badge } from "@/components/atoms/badge";
import { AlertTriangle } from "lucide-react";
import { toastHandler } from "@/lib/ToastHandler";

interface BranchDriverAssignmentConfirmationProps {
  trigger: React.ReactNode;
  branch: {
    id: number;
    name: string;
    code: string;
  };
  selectedDriverIds: number[];
  onSuccess: () => void;
  onClose?: () => void;
}

export default function BranchDriverAssignmentConfirmation({
  trigger,
  branch,
  selectedDriverIds,
  onSuccess,
  onClose,
}: BranchDriverAssignmentConfirmationProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);

  const handleConfirmAssignment = async () => {
    try {
      setIsAssigning(true);

      // Here you would call the actual API to assign drivers to the branch
      // For now, we'll just simulate success
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Show success message
      toastHandler({
        title: "Conductores asignados exitosamente",
        description: `${selectedDriverIds.length} conductor(es) han sido asignados a la sucursal ${branch.name}`,
        variant: "success",
      });

      // Call success callback
      onSuccess();

      // Close dialog
      setIsDialogOpen(false);
      onClose?.();
    } catch (error) {
      console.error("Error assigning drivers to branch:", error);
      toastHandler({
        title: "Error al asignar conductores",
        description:
          "Hubo un problema al asignar los conductores a la sucursal",
        variant: "destructive",
      });
    } finally {
      setIsAssigning(false);
    }
  };

  const handleCancel = () => {
    setIsDialogOpen(false);
    onClose?.();
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className="bg-white max-w-md mx-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            Confirmar Asignación de Conductores
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            ¿Estás seguro de que quieres asignar {selectedDriverIds.length}{" "}
            conductor(es) a esta sucursal?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Branch Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">
              Sucursal #{branch.id}
            </h4>
            <div className="text-sm text-gray-600 space-y-1">
              <p>
                <span className="font-medium">Nombre:</span> {branch.name}
              </p>
              <p>
                <span className="font-medium">Código:</span> {branch.code}
              </p>
              <p>
                <span className="font-medium">Conductores a asignar:</span>
                <Badge variant="outline" className="ml-2">
                  {selectedDriverIds.length}
                </Badge>
              </p>
            </div>
          </div>

          {/* Warning Message */}
          <div className="flex gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-800">
              Esta acción asignará {selectedDriverIds.length} conductor(es) a la
              sucursal {branch.name} y no se puede deshacer fácilmente.
            </p>
          </div>
        </div>

        <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:gap-3">
          <Button
            variant="ghost"
            onClick={handleCancel}
            className="w-full sm:w-auto px-4 py-2.5 h-10 border border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 text-sm font-medium"
          >
            Cancelar
          </Button>
          <Button
            variant="default"
            onClick={handleConfirmAssignment}
            disabled={isAssigning}
            className="w-full sm:w-auto px-4 py-2.5 h-10 bg-indigo-600 text-white hover:bg-indigo-700 transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            {isAssigning ? (
              <div className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Asignando...</span>
              </div>
            ) : (
              `Asignar (${selectedDriverIds.length})`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
